import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/lib/authContext";

const FanContext = createContext(null);

/**
 * FanProvider — loads the currently-authenticated user's fan profile.
 * Falls back to `null` if the user is not authenticated (public views can still render).
 */
export const FanProvider = ({ children }) => {
    const { user } = useAuth();
    const [fan, setFan] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!user || typeof user !== "object") {
            setFan(null);
            setLoading(false);
            return;
        }
        // Skip fetching when the user hasn't activated Fan Passport —
        // avoids a guaranteed 403 on every page load for Artist-primary
        // accounts. The server is still the source of truth; this is
        // purely a network optimisation for the AuthContext state we
        // already have in hand.
        if (!user.has_fan_passport) {
            setFan(null);
            setLoading(false);
            return;
        }
        try {
            const f = await endpoints.fanMe();
            setFan(f);
        } catch {
            setFan(null);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        setLoading(true);
        refresh();
    }, [refresh]);

    const isFollowing = useCallback((artistId) => !!fan?.followed_artist_ids?.includes(artistId), [fan]);
    const isFollowingVenue = useCallback((venueId) => !!fan?.followed_venue_ids?.includes(venueId), [fan]);
    const isSaved = useCallback((eventId) => !!fan?.saved_event_ids?.includes(eventId), [fan]);
    const isRegistered = useCallback((eventId) => !!fan?.registered_event_ids?.includes(eventId), [fan]);
    const isAttended = useCallback((eventId) => !!fan?.attended_event_ids?.includes(eventId), [fan]);

    const follow = async (artistId, source, sourceEventId) => {
        await endpoints.fanFollow(artistId, source, sourceEventId);
        await refresh();
    };
    const unfollow = async (artistId) => {
        await endpoints.fanUnfollow(artistId);
        await refresh();
    };
    const followVenue = async (venueId, source, sourceEventId) => {
        await endpoints.fanFollowVenue(venueId, source, sourceEventId);
        await refresh();
    };
    const unfollowVenue = async (venueId) => {
        await endpoints.fanUnfollowVenue(venueId);
        await refresh();
    };
    const save = async (eventId) => {
        await endpoints.fanSaveEvent(eventId);
        await refresh();
    };
    const unsave = async (eventId) => {
        await endpoints.fanUnsaveEvent(eventId);
        await refresh();
    };
    const register = async (eventId) => {
        await endpoints.fanRegister(eventId);
        await refresh();
    };
    const cancelRegistration = async (eventId) => {
        await endpoints.fanCancelRegistration(eventId);
        await refresh();
    };

    return (
        <FanContext.Provider
            value={{ fan, loading, refresh, isFollowing, isFollowingVenue, isSaved, isRegistered, isAttended, follow, unfollow, followVenue, unfollowVenue, save, unsave, register, cancelRegistration }}
        >
            {children}
        </FanContext.Provider>
    );
};

export const useFan = () => {
    const ctx = useContext(FanContext);
    if (!ctx) throw new Error("useFan must be used inside FanProvider");
    return ctx;
};
